import { Field } from '@linktr.ee/arbor/components/Field';
import { cn } from '@linktr.ee/arbor/utils/cn';
import {
  type FocusEvent,
  forwardRef,
  type InputHTMLAttributes,
  memo,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

export type InputTextProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'disabled'
> & {
  className?: string;
  state?: 'disabled' | 'default' | 'error';
  name?: string;
  id?: string;
  label: string;
  showEditButton?: boolean;
  value?: string;
  prefix?: ReactNode;
  icon?: ReactNode;
  errorMessage?: ReactNode;
  disabled?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onEdit?: () => void;
};

export const InputText = memo(
  forwardRef<HTMLInputElement, InputTextProps>(
    (
      {
        className,
        id,
        name = 'input',
        state = 'default',
        label,
        showEditButton = false,
        disabled,
        required,
        value = '',
        prefix,
        icon,
        errorMessage,
        onEdit,
        type = 'text',
        ...props
      },
      ref
    ) => {
      const { t } = useTranslation();
      const fieldId = useMemo(() => id || name, [id, name]);
      const fieldState = disabled || state === 'disabled' ? 'disabled' : state;
      const showEditButtonNow =
        showEditButton && value.length > 0 && fieldState === 'disabled';

      const handleEdit = useCallback(() => onEdit?.(), [onEdit]);

      return (
        <Field.Root
          id={fieldId}
          state={fieldState}
          className="w-full"
          data-testid="input-text-root"
        >
          <Field.Control
            className="overflow-clip"
            data-testid="input-text-wrapper"
          >
            {prefix && <Field.Addon align="start">{prefix}</Field.Addon>}
            <Field.Label data-testid="input-text-floating-label">
              {label}
            </Field.Label>
            <Field.Input
              {...props}
              ref={ref}
              name={name}
              type={type}
              value={value}
              required={required}
              aria-required={required}
              className={cn(className)}
              data-testid="input-text-input"
              data-state={fieldState}
            />
            {showEditButtonNow ? (
              <Field.Addon align="end">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="text-body-small-medium text-primary transition-colors hover:text-accent focus:outline-none"
                  data-testid="input-edit-button"
                >
                  {t('Edit')}
                </button>
              </Field.Addon>
            ) : (
              icon && <Field.Addon align="end">{icon}</Field.Addon>
            )}
          </Field.Control>
          {errorMessage && <Field.Message>{errorMessage}</Field.Message>}
        </Field.Root>
      );
    }
  )
);
InputText.displayName = 'InputText';
